import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import Homepage from "./components/Homepage";
import PricingPage from "./components/PricingPage";
import Dashboard from "./components/Dashboard";
import ResetPassword from "./components/ResetPassword";
import CalendarPage from "./components/calendar/CalendarPage";
import CalendarCallback from "./components/calendar/CalendarCallback";
import GoogleBusinessCallback from "./pages/GoogleBusinessCallback";
import EmailCallback from "./components/messages/EmailCallback";
import NotificationsPage from "./components/notifications/NotificationsPage";
import LeadsPage from "./components/intake/LeadsPage";
import IntakeFormsPage from "./components/intake/IntakeFormsPage";
import SettingsPage from "./components/settings/SettingsPage";
import AuditLogPage from "./components/settings/AuditLogPage";
import StripeConnectSection from "./components/settings/StripeConnectSection";
import PaymentSettingsSection from "./components/settings/PaymentSettingsSection";
import SubscriptionPage from "./components/settings/SubscriptionPage";
import BrandingSettings from "./components/settings/BrandingSettings";
import FirmProfilePage from "./components/settings/FirmProfilePage";
import DocumentNumberingPage from "./components/settings/DocumentNumberingPage";
import UserManagementPage from "./components/settings/UserManagementPage";
import RoleManagementPage from "./components/settings/RoleManagementPage";
import EmailSignaturePage from "./components/settings/EmailSignaturePage";
import TaxSettingsPage from "./components/settings/TaxSettingsPage";
import MessageTemplatesPage from "./components/settings/MessageTemplatesPage";
import DocumentsPage from "./components/documents/DocumentsPage";
import TemplatesPage from "./components/documents/TemplatesPage";
import EsignPage from "./components/esign/EsignPage";
import BroadcastsPage from "./components/broadcasts/BroadcastsPage";
import EsignEnvelopeDetail from "./components/esign/EsignEnvelopeDetail";
import SignDocumentPage from "./pages/SignDocumentPage";
import AppLayout from "./components/layouts/AppLayout";
import { ToastHost } from "./components/ui/Toast";
import ClientDetail from "./components/clients/ClientDetail";
import ContactsPage from "./components/contacts/ContactsPage";
import PeoplePage from "./components/contacts/PeoplePage";
import MattersPage from "./components/matters/MattersPage";
import MatterDetail from "./components/matters/MatterDetail";
import MessagesPage from "./components/messages/MessagesPage";
import AutomationSettingsPage from "./components/messages/AutomationSettingsPage";
import PhoneNumbersPage from "./components/messages/PhoneNumbersPage";
import EmailConnectionsPage from "./components/messages/EmailConnectionsPage";
import PortingGuidePage from "./components/messages/PortingGuidePage";
import BillingPage from "./components/billing/BillingPage";
import TimeTrackingPage from "./components/time-tracking/TimeTrackingPage";
import RateTablesPage from "./components/billing/RateTablesPage";
import FeeItemsPage from "./components/billing/FeeItemsPage";
import TrustPage from "./components/trust/TrustPage";
import BankAccountsPage from "./components/trust/BankAccountsPage";
import ChartOfAccountsPage from "./components/trust/ChartOfAccountsPage";
import ReconciliationPage from "./components/trust/ReconciliationPage";
import AccountingPage from "./components/accounting/AccountingPage";
import TransactionDetail from "./components/real-estate/TransactionDetail";
import IPDocketPage from "./components/ip/IPDocketPage";
import IPPortfolioDashboard from "./components/ip/IPPortfolioDashboard";
import ReportsPage from "./components/reports/ReportsPage";
import ConflictCheckPage from "./components/conflicts/ConflictCheckPage";
import BillingDocumentBuilder from "./components/billing/builder/BillingDocumentBuilder";
import PayInvoicePage from "./pages/PayInvoicePage";
import BookingPage from "./pages/BookingPage";
import ManageBookingPage from "./pages/ManageBookingPage";
import EmbedBookingPage from "./pages/EmbedBookingPage";
import EmbedFormPage from "./pages/EmbedFormPage";
import EmbedReviewsPage from "./pages/EmbedReviewsPage";
import ReviewPage from "./pages/ReviewPage";
import ReviewsPage from "./components/reviews/ReviewsPage";
import BookDemoPage from "./pages/BookDemoPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import WidgetsPage from "./components/widgets/WidgetsPage";
import OnboardingPage from "./components/onboarding/OnboardingPage";
import AcceptInvite from "./components/AcceptInvite";
import FeatureGate from "./components/ui/FeatureGate";
import TermsOfService from "./components/legal/TermsOfService";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import { PRACTICE_AREA_SLUGS, SLUG_TO_PRACTICE_AREA } from "./components/matters/practiceAreaTabMap";
import api from "./services/api";
import { useUserDataContext } from "./context/UserDataContext";
import { EmailComposeProvider } from "./contexts/EmailComposeContext";
import { MatterInspectorProvider } from "./contexts/MatterInspectorContext";
import { RunningTimerProvider } from "./contexts/RunningTimerContext";
import EmailComposePopup from "./components/messages/EmailComposePopup";
import { GuidedTourProvider } from "./contexts/GuidedTourContext";
import SpotlightOverlay from "./components/guided-tour/SpotlightOverlay";
import { AssistantHost } from "./components/assistant";
import { usePageTracker } from "./hooks/usePageTracker";

/** Silent component that tracks page visits for analytics */
function PageTracker() {
  usePageTracker();
  return null;
}

/** Redirect legacy /immigration/:id and /practice-area/:id URLs to /matters/:id */
function RedirectToMatter() {
  const { id } = useParams();
  return <Navigate to={`/matters/${id}`} replace />;
}

/** Redirect legacy practice area routes (e.g. /criminal) to /matters?tab=<key> */
function RedirectToPracticeAreaTab() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").split("/")[0];
  const practiceAreaKey = SLUG_TO_PRACTICE_AREA[slug];
  if (practiceAreaKey) {
    return <Navigate to={`/matters?tab=${practiceAreaKey}`} replace />;
  }
  return <Navigate to="/matters" replace />;
}

interface StripeData {
  customerId: string | null;
  selectedPlan: string | null;
  trialExpiration?: Date | undefined;
}

interface AppUserData {
  _id: string; // Add any other necessary properties
  email: string;
  subscriptionStatus: "free trial" | "basic" | "pro" | "elite";
  stripe: StripeData;
  isOnTrial: boolean;
}

// Defined outside App so React reuses the same DOM node across re-renders,
// preventing the CSS animation from restarting on every state change.
const AuthLoadingScreen: React.FC = () => (
  <div
    className="min-h-screen flex flex-col items-center justify-center gap-4"
    style={{ backgroundColor: "#f0fdf4" }}
  >
    <div
      className="animate-spin h-10 w-10 rounded-full border-4"
      style={{ borderColor: "#0F5132", borderTopColor: "transparent" }}
    />
    <p className="text-sm font-medium" style={{ color: "#0F5132" }}>
      Loading your workspace…
    </p>
  </div>
);

function App() {
  const { refreshUserData, refreshFirmData } = useUserDataContext();

  const [isLoggedIn, setIsLoggedIn] = useState(
    () => !!localStorage.getItem("accessToken")
  );
  const [userData, setUserData] = useState<AppUserData>({
    _id: "",
    email: "",
    subscriptionStatus: "free trial",
    stripe: {
      customerId: null,
      selectedPlan: null,
      trialExpiration: undefined,
    },
    isOnTrial: false,
  });
  const [isOnTrial, setIsOnTrial] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState<boolean | null>(null);

  // Log out reactively when the token is removed (e.g. by the 401 interceptor)
  useEffect(() => {
    const checkToken = () => {
      if (!localStorage.getItem("accessToken") && isLoggedIn) {
        setIsLoggedIn(false);
        setNeedsOnboarding(null);
      }
    };
    // Listen for cross-tab storage changes
    window.addEventListener("storage", checkToken);
    // Poll every 5s  -  1s was too aggressive and could interrupt post-signup flow
    const interval = setInterval(checkToken, 5000);
    return () => {
      window.removeEventListener("storage", checkToken);
      clearInterval(interval);
    };
  }, [isLoggedIn]);
  const trialExpirationDate = userData.stripe.trialExpiration;
  const trialExpiresAt =
    trialExpirationDate instanceof Date ? trialExpirationDate.getTime() : 0;

  // Check onboarding status when user is logged in
  useEffect(() => {
    if (!isLoggedIn) {
      setNeedsOnboarding(null);
      return;
    }
    api
      .get("/api/firm/onboarding/status")
      .then((res) => {
        setNeedsOnboarding(!res.data?.completed);
      })
      .catch(() => {
        // If the endpoint fails (e.g. old firm without onboarding field), skip onboarding
        setNeedsOnboarding(false);
      });
  }, [isLoggedIn]);

  const handleSignIn = (data: { token: string; userId: string }) => {
    localStorage.setItem("accessToken", data.token);
    setIsLoggedIn(true);
    // Fetch user/firm data so the sidebar shows the real name immediately
    refreshUserData();
  };

  const handleSignOut = () => {
    localStorage.removeItem("accessToken");

    // Set isLoggedIn state to false
    setIsLoggedIn(false);
    setNeedsOnboarding(null);
    // Reset user data state
    setUserData({
      _id: "",
      email: "",
      subscriptionStatus: "free trial",
      stripe: {
        customerId: null,
        selectedPlan: null,
        trialExpiration: undefined,
      },
      isOnTrial: false,
    });
  };

  return (
    <EmailComposeProvider>
    <Router>
      <RunningTimerProvider>
      <MatterInspectorProvider>
      <GuidedTourProvider isLoggedIn={isLoggedIn} onboardingComplete={needsOnboarding === false}>
      <Routes>
        {/* Public pages  -  accessible regardless of auth state */}
        <Route path="/pay/:token" element={<PayInvoicePage />} />
        <Route path="/pay/:token/success" element={<PayInvoicePage />} />
        <Route path="/sign/:token" element={<SignDocumentPage />} />
        <Route path="/book/:firmSlug/:bookingSlug" element={<BookingPage />} />
        <Route path="/book/manage/:manageToken" element={<ManageBookingPage />} />
        <Route path="/embed/booking/:firmSlug/:bookingSlug" element={<EmbedBookingPage />} />
        <Route path="/embed/form/:firmSlug/:formSlug" element={<EmbedFormPage />} />
        <Route path="/embed/reviews/:firmSlug" element={<EmbedReviewsPage />} />
        <Route path="/review/:token" element={<ReviewPage />} />
        <Route path="/book-demo" element={<BookDemoPage />} />
        <Route path="/kavoa-admin/login" element={<AdminLoginPage />} />
        <Route path="/kavoa-admin" element={<AdminDashboard />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {!isLoggedIn ? (
          <>
            <Route
              path="/"
              element={<Homepage onSuccessfulLogin={handleSignIn} />}
            />
            <Route
              path="/pricing"
              element={
                <PricingPage
                  onOpenSignup={() => {}}
                  onOpenLogin={() => {}}
                  onSuccessfulLogin={handleSignIn}
                />
              }
            />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route
              path="/accept-invite/:token"
              element={
                <AcceptInvite
                  onAccepted={(jwt, user) => {
                    handleSignIn({ token: jwt, userId: user.id });
                  }}
                />
              }
            />
            <Route path="*" element={<Navigate to="/" />} />
          </>
        ) : needsOnboarding === null ? (
          /* Onboarding status still loading  -  show a spinner instead of
             flashing the Dashboard while the API call is in flight */
          <Route path="*" element={<AuthLoadingScreen />} />
        ) : needsOnboarding ? (
          /* Firm has not completed onboarding  -  only the wizard is
             reachable; every other path redirects into it.
             We do NOT render the Dashboard / app routes here so that
             React Router's path-specificity ranking cannot bypass the
             catch-all redirect. */
          <>
            <Route
              path="/onboarding"
              element={
                <OnboardingPage
                  onComplete={() => { setNeedsOnboarding(false); refreshFirmData(); }}
                  onSignOut={handleSignOut}
                />
              }
            />
            <Route path="*" element={<Navigate to="/onboarding" replace />} />
          </>
        ) : (
          <>
            {/* Onboarding route  -  still reachable so users can revisit */}
            <Route
              path="/onboarding"
              element={
                <OnboardingPage
                  onComplete={() => { setNeedsOnboarding(false); refreshFirmData(); }}
                  onSignOut={handleSignOut}
                />
              }
            />

            {/* Dashboard (own layout with sidebar already built in) */}
            <Route
              path="/"
              element={
                <Dashboard
                  handleSignOut={handleSignOut}
                  customerId={userData.stripe.customerId}
                  priceId={userData.stripe.selectedPlan}
                  subscriptionStatus={userData.subscriptionStatus}
                  trialExpiresAt={trialExpiresAt}
                />
              }
            />

            {/* CRM Routes  -  wrapped in AppLayout (shared sidebar) */}
            <Route
              path="/matters"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <MattersPage />
                </AppLayout>
              }
            />
            <Route
              path="/matters/:id"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <MatterDetail />
                </AppLayout>
              }
            />
            <Route
              path="/clients"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <PeoplePage />
                </AppLayout>
              }
            />
            <Route
              path="/clients/:id"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <ClientDetail />
                </AppLayout>
              }
            />
            <Route
              path="/contacts/:id"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <ContactsPage />
                </AppLayout>
              }
            />

            {/* Communications Routes */}
            <Route
              path="/communications"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <FeatureGate requiredPlan="pro" featureName="Communications">
                    <MessagesPage />
                  </FeatureGate>
                </AppLayout>
              }
            />
            <Route
              path="/communications/automations"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <FeatureGate requiredPlan="pro" featureName="Automations">
                    <AutomationSettingsPage />
                  </FeatureGate>
                </AppLayout>
              }
            />
            <Route
              path="/settings/phone-numbers"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <PhoneNumbersPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/email-connections"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <EmailConnectionsPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/port-guide"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <PortingGuidePage />
                </AppLayout>
              }
            />

            {/* Time Tracking */}
            <Route
              path="/time-tracking"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <TimeTrackingPage />
                </AppLayout>
              }
            />

            {/* Billing Routes */}
            <Route
              path="/billing"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <BillingPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/rate-tables"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <RateTablesPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/fee-items"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <FeeItemsPage />
                </AppLayout>
              }
            />

            <Route
              path="/billing/invoices/:id/builder"
              element={
                <BillingDocumentBuilder documentType="invoice" />
              }
            />
            <Route
              path="/billing/statements/:id/builder"
              element={
                <BillingDocumentBuilder documentType="account_summary" />
              }
            />

            {/* Accounting Routes */}
            <Route
              path="/accounting"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <FeatureGate requiredPlan="elite" featureName="Accounting">
                    <AccountingPage />
                  </FeatureGate>
                </AppLayout>
              }
            />
            <Route path="/trust" element={<Navigate to="/accounting" replace />} />
            <Route
              path="/settings/bank-accounts"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <BankAccountsPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/chart-of-accounts"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <ChartOfAccountsPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/reconciliation"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <ReconciliationPage />
                </AppLayout>
              }
            />

            {/* Immigration Routes */}
            <Route
              path="/immigration"
              element={<Navigate to="/matters?tab=immigration" replace />}
            />
            <Route
              path="/immigration/:id"
              element={<RedirectToMatter />}
            />

            {/* Real Estate Routes */}
            <Route
              path="/real-estate"
              element={<Navigate to="/matters?tab=real_estate" replace />}
            />
            <Route
              path="/real-estate/:id"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <TransactionDetail />
                </AppLayout>
              }
            />

            {/* IP Routes */}
            <Route
              path="/ip/docket"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <IPDocketPage />
                </AppLayout>
              }
            />
            <Route
              path="/ip/portfolio"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <IPPortfolioDashboard />
                </AppLayout>
              }
            />

            {/* Practice Area Redirects — legacy URLs redirect to /matters?tab=<key> */}
            {Object.values(PRACTICE_AREA_SLUGS).map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<RedirectToPracticeAreaTab />} />
            ))}

            {/* Reports */}
            <Route
              path="/reports"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <ReportsPage />
                </AppLayout>
              }
            />

            {/* Conflict Checks */}
            <Route
              path="/conflict-checks"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <ConflictCheckPage />
                </AppLayout>
              }
            />

            {/* Reviews */}
            <Route
              path="/reviews"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <ReviewsPage />
                </AppLayout>
              }
            />

            {/* Website Widgets (under Settings) */}
            <Route
              path="/settings/widgets"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <WidgetsPage />
                </AppLayout>
              }
            />

            {/* Calendar */}
            <Route
              path="/calendar"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <CalendarPage />
                </AppLayout>
              }
            />
            <Route
              path="/calendar/callback"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <CalendarCallback />
                </AppLayout>
              }
            />
            <Route
              path="/email/callback"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <EmailCallback />
                </AppLayout>
              }
            />
            <Route
              path="/integrations/google-business/callback"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <GoogleBusinessCallback />
                </AppLayout>
              }
            />

            {/* Notifications */}
            <Route
              path="/notifications"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <NotificationsPage />
                </AppLayout>
              }
            />

            {/* Leads / Intake */}
            <Route
              path="/leads"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <FeatureGate requiredPlan="pro" featureName="Leads & Intake">
                    <LeadsPage />
                  </FeatureGate>
                </AppLayout>
              }
            />

            {/* Settings Hub */}
            <Route
              path="/settings"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <SettingsPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/subscription"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <SubscriptionPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/intake-forms"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <IntakeFormsPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/audit-log"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <AuditLogPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/stripe-connect"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <StripeConnectSection />
                </AppLayout>
              }
            />
            <Route
              path="/settings/payment-methods"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <PaymentSettingsSection />
                </AppLayout>
              }
            />
            <Route
              path="/settings/branding"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <BrandingSettings />
                </AppLayout>
              }
            />
            <Route
              path="/settings/firm-profile"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <FirmProfilePage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/document-numbering"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <DocumentNumberingPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/users"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <UserManagementPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/roles"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <RoleManagementPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/email-signature"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <EmailSignaturePage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/tax"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <TaxSettingsPage />
                </AppLayout>
              }
            />
            <Route
              path="/settings/message-templates"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <MessageTemplatesPage />
                </AppLayout>
              }
            />

            {/* Existing routes */}
            <Route
              path="/documents"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <DocumentsPage />
                </AppLayout>
              }
            />
            <Route
              path="/documents/templates"
              element={<Navigate to="/documents" replace />}
            />
            <Route
              path="/esign"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <FeatureGate requiredPlan="pro" featureName="E-Signatures">
                    <EsignPage />
                  </FeatureGate>
                </AppLayout>
              }
            />
            <Route
              path="/esign/:id"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <EsignEnvelopeDetail />
                </AppLayout>
              }
            />
            <Route
              path="/broadcasts"
              element={
                <AppLayout handleSignOut={handleSignOut}>
                  <FeatureGate requiredPlan="pro" featureName="Broadcasts">
                    <BroadcastsPage />
                  </FeatureGate>
                </AppLayout>
              }
            />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route
              path="/accept-invite/:token"
              element={
                <AcceptInvite
                  onAccepted={(jwt, user) => {
                    handleSignIn({ token: jwt, userId: user.id });
                  }}
                />
              }
            />

            {/* Catch-all: redirect unknown routes to Dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
      <SpotlightOverlay />
      {isLoggedIn && needsOnboarding === false && <AssistantHost />}
      <PageTracker />
      </GuidedTourProvider>
      </MatterInspectorProvider>
      </RunningTimerProvider>
    </Router>
    <EmailComposePopup />
    <ToastHost />
    </EmailComposeProvider>
  );
}

export default App;
